import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { SlideModal } from '../../../../../components/abstract/modal/slide/SlideModal';
import { Icon } from '../../../../../components/common/icon/Icon';
import { CheckboxInput } from '../../../../../components/common/inputs/CheckboxInput';
import { termsOfUse } from '../../../../../constants/termsOfUse/termsOfUse.constant';
import { useAppDispatch } from '../../../../../hooks/store/useStore';
import { userJoinSlice } from '../../../../../store/userJoinSlice';
import { JoinProcessPageWrapper } from '../JoinProcessPageWrapper';
import { Tooltip } from '../../../../../components/common/tooltip/Tooltip';

export const JoinProcessAgreementPage = () => {
  const [isAgreementAllChecked, setIsAgreementAllChecked] = useState<boolean>(false);
  const [isPluvaMarketTermAgreed, setIsPluvaMarketTermAgreed] = useState<boolean>(false);
  const [isPersonalInformationTermAgreed, setIsPersonalInformationTermAgreed] = useState<boolean>(false);
  const [isAgeTermAgreed, setIsAgeTermAgreed] = useState<boolean>(false);
  const [isAdvertisementTermAgreed, setIsAdvertisementTermAgreed] = useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userJoinActions = userJoinSlice.actions;

  const canMoveNextPage = useMemo<boolean>(
    () => isPluvaMarketTermAgreed && isPersonalInformationTermAgreed && isAgeTermAgreed,
    [isPluvaMarketTermAgreed, isPersonalInformationTermAgreed, isAgeTermAgreed],
  );

  const [isPersonalInfomationModalOpen, setIsPersonalInfomationModalOpen] = useState<boolean>(false);
  const [isPluvaMarketModalOpen, setIsPluvaMarketModalOpen] = useState<boolean>(false);
  const [isMarketingModalOpen, setIsMarketingModalOpen] = useState<boolean>(false);

  const handleAgreeAllChange = (isChecked: boolean) => {
    let value;

    if (isChecked) {
      value = false;
    } else {
      value = true;
    }

    setIsPluvaMarketTermAgreed(value);
    setIsPersonalInformationTermAgreed(value);
    setIsAgeTermAgreed(value);
    setIsAdvertisementTermAgreed(value);
  };

  useEffect(() => {
    if (isAgreementAllChecked) {
      if (
        !(isPluvaMarketTermAgreed && isPersonalInformationTermAgreed && isAgeTermAgreed && isAdvertisementTermAgreed)
      ) {
        setIsAgreementAllChecked(false);
      }
    }

    if (!isAgreementAllChecked) {
      if (isPluvaMarketTermAgreed && isPersonalInformationTermAgreed && isAgeTermAgreed && isAdvertisementTermAgreed) {
        setIsAgreementAllChecked(true);
      }
    }
  }, [
    isAgreementAllChecked,
    isPluvaMarketTermAgreed,
    isPersonalInformationTermAgreed,
    isAgeTermAgreed,
    isAdvertisementTermAgreed,
  ]);

  useEffect(() => {
    dispatch(userJoinActions.changeIsJoinProcessStartedProperly(true));
  }, [dispatch, userJoinActions]);

  return (
    <>
      <JoinProcessPageWrapper
        headerTitle={'이용약관, 개인정보 수집 및 이용안내'}
        description={'필수 항목에 동의하지 않으실 경우\n서비스를 이용 하실 수 없습니다.'}
        canConfirm={canMoveNextPage}
        confirmHandler={() => {
          dispatch(userJoinActions.changeAdvertisementTermsAgreement(isAdvertisementTermAgreed));
          navigate('/join-process/region', { replace: true });
        }}
      >
        <InputSection>
          <AgreeAllInputWrap>
            <CheckboxInput
              id={'isAgreementAllChecked'}
              onChange={() => {
                handleAgreeAllChange(isAgreementAllChecked);
              }}
              isChecked={isAgreementAllChecked}
            />
            <Label htmlFor={'isAgreementAllChecked'}>
              <LabelBoldText>{'전체동의'}</LabelBoldText>
            </Label>
          </AgreeAllInputWrap>

          <AgreeInputWrap>
            <AgreeInputLeftSection>
              <CheckboxInput
                id={'isPluvaMarketTermAgreed'}
                onChange={() => setIsPluvaMarketTermAgreed(!isPluvaMarketTermAgreed)}
                isChecked={isPluvaMarketTermAgreed}
              />
              <Label htmlFor={'isPluvaMarketTermAgreed'}>
                <RedChip>
                  <RedChipText>{'필수'}</RedChipText>
                </RedChip>
                <LabelThinText>{'플루바 마켓 회원 이용약관'}</LabelThinText>
              </Label>
            </AgreeInputLeftSection>
            <AgreeInputRightSection>
              <ArrowButton onClick={() => setIsPluvaMarketModalOpen(true)}>
                <Icon name={'RightArrow'} width={13} height={13} />
              </ArrowButton>
            </AgreeInputRightSection>
          </AgreeInputWrap>

          <AgreeInputWrap>
            <AgreeInputLeftSection>
              <CheckboxInput
                id={'isPersonalInformationTermAgreed'}
                onChange={() => setIsPersonalInformationTermAgreed(!isPersonalInformationTermAgreed)}
                isChecked={isPersonalInformationTermAgreed}
              />
              <Label htmlFor={'isPersonalInformationTermAgreed'}>
                <RedChip>
                  <RedChipText>{'필수'}</RedChipText>
                </RedChip>
                <LabelThinText>{'개인정보 수집 및 이용안내'}</LabelThinText>
              </Label>
            </AgreeInputLeftSection>
            <AgreeInputRightSection>
              <ArrowButton onClick={() => setIsPersonalInfomationModalOpen(true)}>
                <Icon name={'RightArrow'} width={13} height={13} />
              </ArrowButton>
            </AgreeInputRightSection>
          </AgreeInputWrap>

          <AgreeInputWrap>
            <AgreeInputLeftSection>
              <CheckboxInput
                id={'isAgeTermAgreed'}
                onChange={() => setIsAgeTermAgreed(!isAgeTermAgreed)}
                isChecked={isAgeTermAgreed}
              />
              <Label htmlFor={'isAgeTermAgreed'}>
                <RedChip>
                  <RedChipText>{'필수'}</RedChipText>
                </RedChip>
                <LabelThinText>{'만 12세 이상 동의'}</LabelThinText>
              </Label>
            </AgreeInputLeftSection>
          </AgreeInputWrap>

          <AgreeInputWrap>
            <Tooltip
              text={
                <>
                  {'수신 동의하고 '}
                  <StrongText>{'신규 매물 알람'}</StrongText> {'받기!'}
                </>
              }
              size={'large'}
              position={'bottom'}
            >
              <AgreeInputLeftSection>
                <CheckboxInput
                  id={'isAdvertisementTermAgreed'}
                  onChange={() => setIsAdvertisementTermAgreed(!isAdvertisementTermAgreed)}
                  isChecked={isAdvertisementTermAgreed}
                />
                <Label htmlFor={'isAdvertisementTermAgreed'}>
                  <GrayChip>
                    <GrayChipText>{'선택'}</GrayChipText>
                  </GrayChip>
                  <LabelThinText>{'광고성 정보 수신 동의'}</LabelThinText>
                </Label>
              </AgreeInputLeftSection>
            </Tooltip>
          </AgreeInputWrap>
        </InputSection>
      </JoinProcessPageWrapper>

      <SlideModal
        isSlideModalOpen={isPluvaMarketModalOpen}
        closeAll={() => setIsPluvaMarketModalOpen(false)}
        showSlideButton={false}
        showBottomCloseButton
      >
        <ModalContent>
          <TermsOfUseHeader>{termsOfUse.pluvaMarket.header.trim()}</TermsOfUseHeader>
          <TermsOfUseContent>{termsOfUse.pluvaMarket.content.trim()}</TermsOfUseContent>
        </ModalContent>
      </SlideModal>

      <SlideModal
        isSlideModalOpen={isPersonalInfomationModalOpen}
        closeAll={() => setIsPersonalInfomationModalOpen(false)}
        showSlideButton={false}
        showBottomCloseButton
      >
        <ModalContent>
          <TermsOfUseHeader>{termsOfUse.personalinfomation.header.trim()}</TermsOfUseHeader>
          <TermsOfUseContent>{termsOfUse.personalinfomation.content.trim()}</TermsOfUseContent>
        </ModalContent>
      </SlideModal>

      <SlideModal
        isSlideModalOpen={isMarketingModalOpen}
        closeAll={() => setIsMarketingModalOpen(false)}
        showSlideButton={false}
        showBottomCloseButton
      >
        <ModalContent>
          <TermsOfUseHeader>{termsOfUse.marketing.header.trim()}</TermsOfUseHeader>
          <TermsOfUseContent>{termsOfUse.marketing.content.trim()}</TermsOfUseContent>
        </ModalContent>
      </SlideModal>
    </>
  );
};

const InputSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const AgreeAllInputWrap = styled.div`
  width: 100%;

  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  padding: 16px;
  margin-bottom: 16px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const AgreeInputWrap = styled.div`
  width: 100%;
  min-height: 50px;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AgreeInputLeftSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  padding: 16px;

  width: 100%;
`;

const AgreeInputRightSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Label = styled.label`
  width: 100%;

  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  margin-left: 10px;

  cursor: pointer;
`;

const LabelBoldText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 13.6px */
`;

const LabelThinText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 13.6px */
`;

const Chip = styled.div`
  height: 18px;

  display: flex;
  align-items: center;

  padding: 4px 6px;

  gap: 2px;
`;

const RedChip = styled(Chip)`
  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Color-secondary-red, #ffdbe0);
`;

const GrayChip = styled(Chip)`
  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const ChipText = styled.span`
  text-align: center;

  /* System/X-Small/xs-medium */
  font-family: Pretendard;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 8.5px */
`;

const RedChipText = styled(ChipText)`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);
`;

const GrayChipText = styled(ChipText)`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
`;

const ArrowButton = styled.button``;

const ConfirmButton = styled.button`
  width: 100%;
  height: 40px;
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;

  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #196eff);

  &:disabled {
    opacity: 0.3;
  }

  cursor: pointer;
`;

const ConfirmButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const ModalContent = styled.div`
  width: 100%;
  height: 400px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  margin: 24px 0px;

  gap: 24px;
`;

const TermsOfUseHeader = styled.span`
  width: 100%;

  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const TermsOfUseContent = styled.p`
  width: 100%;

  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */

  overflow-y: scroll;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const StrongText = styled.span`
  font-weight: 700;
`;
